/* eslint-disable no-console */
import {
  AppState,
  RedirectLoginOptions,
  useAuth0,
  User,
} from "@auth0/auth0-react";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { ApplicationControlType } from "./types";
import extractRolesAndPermissions from "../../utils";
import { PERMISSIONS, ROLES } from "../../constants";
import { clearCompliance } from "../compliance/utils/compliance-session";

export interface Auth0ContextContextType {
  isAuthenticated: boolean | undefined;
  isLoading: boolean | undefined;
  user: User | undefined;
  userRoles: string[] | null;
  userPermissions: string[] | null;
  hasClientId: boolean;
  hasAccess: (requiredPermission: string | null) => boolean;
  signoutRedirect?: () => Promise<void>;
  signoutRedirectCallback?: () => Promise<void>;
  signinRedirect?: () => Promise<void>;
  signinRedirectCallback?: () => Promise<void>;
  signinSilentCallback?: () => Promise<void>;
  updateUserContext?: () => Promise<void>;
}

const Auth0Context = createContext<Auth0ContextContextType | undefined>(
  undefined,
);

interface IdentityAuth0ProviderProps {
  children: ReactNode;
}

const useAuthActions = (
  loginWithRedirect: (
    options?: RedirectLoginOptions<AppState>,
  ) => Promise<void>,
  logout: (options: { logoutParams: { returnTo: string } }) => void,
  setUserData: React.Dispatch<React.SetStateAction<User | undefined>>,
) => {
  const navigate = useNavigate();

  return {
    signinRedirect: async () =>
      loginWithRedirect({
        appState: {
          returnTo: window.location.pathname + window.location.search,
        },
      }),
    signinRedirectCallback: async () => {
      navigate(window.location);
    },
    signinSilentCallback: async () =>
      console.log("signinSilentCallback not implemented yet"),
    signoutRedirect: async () => {
      clearCompliance();
      setUserData(undefined);
      logout({ logoutParams: { returnTo: window.location.origin } });
    },
    signoutRedirectCallback: async () =>
      console.log("signoutRedirectCallback not implemented yet"),
    updateUserContext: async () =>
      console.log("updateUserContext not implemented yet"),
  };
};

const useSyncUserData = (
  user: User | undefined,
  setUserData: React.Dispatch<React.SetStateAction<User | undefined>>,
  isMountedRef: React.MutableRefObject<boolean>,
) => {
  useEffect(() => {
    setUserData(user);
    const ref = isMountedRef;
    return () => {
      ref.current = false;
    };
  }, [user, isMountedRef, setUserData]);
};

const computeAccess = (
  requiredPermission: string | null,
  userPermissions: string[] | null,
  userRoles: string[] | null,
): boolean => {
  if (!requiredPermission) return false;
  const hasAnalystAccess =
    !!userRoles?.includes(ROLES.MDA_ANALYST_USER) &&
    (requiredPermission === PERMISSIONS.VIEW_SEARCH ||
      requiredPermission === PERMISSIONS.VIEW_WATCHLIST);

  return (
    hasAnalystAccess || (userPermissions?.includes(requiredPermission) ?? false)
  );
};

const IdentityAuth0Provider = ({ children }: IdentityAuth0ProviderProps) => {
  const {
    user,
    loginWithRedirect,
    isAuthenticated,
    isLoading: auth0Loading,
    logout,
  } = useAuth0();

  const [userData, setUserData] = useState<User | undefined>();

  const isMountedRef = useRef(true);

  const authActions = useAuthActions(loginWithRedirect, logout, setUserData);

  useSyncUserData(user, setUserData, isMountedRef);

  const { userRoles, userPermissions, clientIds } = useMemo(() => {
    if (!userData?.application_controls) {
      return {
        userRoles: null,
        userPermissions: null,
        clientIds: null,
      };
    }

    const controls: ApplicationControlType = JSON.parse(
      userData.application_controls,
    )[0];

    return extractRolesAndPermissions(controls);
  }, [userData?.application_controls]);

  const isLoading = auth0Loading || !userData?.email;

  const hasAccess = useCallback(
    (requiredPermission: string | null, permissions?: string[] | null) =>
      computeAccess(
        requiredPermission,
        permissions ?? userPermissions,
        userRoles,
      ) && !!clientIds?.length,
    [userPermissions, userRoles, clientIds],
  );

  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      user: userData,
      userRoles,
      userPermissions,
      hasClientId: !!clientIds?.length,
      hasAccess,
      ...authActions,
    }),
    [
      isAuthenticated,
      isLoading,
      userData,
      userRoles,
      userPermissions,
      clientIds,
      authActions,
      hasAccess,
    ],
  );

  return (
    <Auth0Context.Provider value={contextValue}>
      {children}
    </Auth0Context.Provider>
  );
};

const useAuth = () => {
  const context = useContext(Auth0Context);

  if (context === undefined) {
    throw new Error("useAuth0 must be used within an Auth0Provider");
  }
  return context;
};

export { Auth0Context, IdentityAuth0Provider, useAuth };
